/**
 * useChartCaptureRefs - Hook for managing chart capture refs
 *
 * Handles ref creation and registration with the ChartCaptureProvider
 * for PDF generation on web platform.
 * @remarks Web-only functionality, returns undefined refs on native
 */

import { useEffect, useRef } from "react";
import { Platform } from "react-native";

import { useChartCaptureOptional } from "../providers/ChartCaptureProvider";

/**
 * Return type for chart capture refs hook
 */
interface ChartCaptureRefs {
  /** Ref for spider chart container (undefined on native) */
  readonly spiderChartRef: React.RefObject<HTMLDivElement | null> | undefined;
  /** Ref for dot plot container (undefined on native) */
  readonly dotPlotRef: React.RefObject<HTMLDivElement | null> | undefined;
}

/**
 * Hook that creates and registers chart refs for PDF capture
 * Registers ref objects (not .current) so the provider can read DOM elements at capture time
 * @returns Object containing refs for spider chart and dot plot
 */
export function useChartCaptureRefs(): ChartCaptureRefs {
  const spiderChartRef = useRef<HTMLDivElement | null>(null);
  const dotPlotRef = useRef<HTMLDivElement | null>(null);
  const { registerSpiderChartRef, registerDotPlotRef } =
    useChartCaptureOptional();

  useEffect(() => {
    if (Platform.OS === "web") {
      registerSpiderChartRef(spiderChartRef);
      registerDotPlotRef(dotPlotRef);
    }
  }, [registerSpiderChartRef, registerDotPlotRef]);

  return {
    spiderChartRef: Platform.OS === "web" ? spiderChartRef : undefined,
    dotPlotRef: Platform.OS === "web" ? dotPlotRef : undefined,
  };
}
